import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { approvePaybyExtension, declinePaybyExtension } from '@/domain/paybyExtensionFlow';
import { linkSenderRole, missedLinkOwner } from '@/domain/missedLinks';
import { actorLabel } from '@/domain/actor';
import { aud } from '@/utils/format';
import { studioDayKey, studioDayWithTimeOf } from '@atink/domain';
import { formatStudioDay } from '@/utils/studioDay';
import type { OpsPaybyExtensionItem } from '@/domain/opsWorklist';

const OWNER_LABEL = { agent: 'the Agent', ops: 'Ops', sweeper: 'the Sweeper' } as const;

/** The Melbourne studio day (`YYYY-MM-DD`) for a date input, in any browser zone (88v6). */
const dateKey = (iso: string) => studioDayKey(iso);
/** A pay-by label ('14 May') on the Melbourne day, in any browser zone (88v6). */
const dayLabel = (iso: string) => formatStudioDay(iso, 'd MMM');

/** The picked Melbourne day, keeping the link's cut-off time of day (88v6). */
const onDay = (baseIso: string, key: string): string => studioDayWithTimeOf(baseIso, key);

/**
 * Ops decides an INSIDE-7-day pay-by extension (W5 i5, DECIDED 24 Sep). Only
 * inside-7-day asks reach Ops; outside 7 days the follow-up owner extends it
 * directly. Extend (the requested date prefilled, editable) moves the link's
 * pay-by and re-points its reminder; Decline needs a reason and the link keeps
 * its original date and lapses as normal.
 */
export default function PayByExtensionBody({
  item,
  by,
  onDone,
}: {
  item: OpsPaybyExtensionItem;
  by: string;
  onDone: () => void;
}) {
  const bookings = useBrainStore((s) => s.bookings);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const { request, deposit, booking } = item;

  const [picked, setPicked] = useState(() => dateKey(request.requestedPayByIso));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const newPayByIso =
    picked === dateKey(request.requestedPayByIso) ? request.requestedPayByIso : onDay(request.requestedPayByIso, picked);
  const extendable = !!picked && Date.parse(newPayByIso) > Date.parse(request.currentPayByIso);
  const owner = deposit ? missedLinkOwner(linkSenderRole(deposit, bookings)) : 'agent';

  const failCopy = (e: string) =>
    e === 'link-closed'
      ? 'The link is no longer unpaid (paid or voided), so there is nothing to extend.'
      : e === 'not-pending'
        ? 'This request was already decided.'
        : "Couldn't find this request - reload and try again.";

  const extend = () => {
    if (!extendable) return;
    const res = approvePaybyExtension({
      requestId: request.id,
      by,
      request: { ...request, requestedPayByIso: newPayByIso },
      ...(deposit ? { deposit } : {}),
    });
    if (!res.ok) return setError(failCopy(res.error));
    pushNotification({
      intensity: 'info',
      title: 'Pay-by extended',
      body: `New pay-by ${dayLabel(newPayByIso)}. Reminder re-pointed.`,
      ttlMs: 4000,
    });
    onDone();
  };

  const decline = () => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    const res = declinePaybyExtension({ requestId: request.id, by, note: trimmed, request });
    if (!res.ok) return setError(failCopy(res.error));
    pushNotification({
      intensity: 'info',
      title: 'Extension declined',
      body: `The link keeps ${dayLabel(request.currentPayByIso)} and lapses as normal.`,
      ttlMs: 4000,
    });
    onDone();
  };

  return (
    <div className="flex flex-col gap-4" data-testid="pbx-body">
      <div className="panel-dark p-3.5 grid grid-cols-2 gap-x-4 gap-y-3">
        <Field k="Appointment" v={booking ? dayLabel(booking.start) : 'No slot held'} />
        <Field
          k="Pay-by"
          v={`${dayLabel(request.currentPayByIso)} → ${dayLabel(request.requestedPayByIso)}`}
        />
        <Field k="Requested by" v={actorLabel(request.requestedBy)} />
        <Field k="Deposit" v={deposit ? aud(deposit.amountAud) : '—'} />
      </div>

      {request.note ? (
        <blockquote className="rounded-md bg-ink-black/30 px-3 py-2.5 font-serif text-sm text-cream">
          &ldquo;{request.note}&rdquo;
        </blockquote>
      ) : null}

      <label className="block max-w-[220px]">
        <span className="label-small-caps">New pay-by</span>
        <input
          type="date"
          value={picked}
          min={dateKey(request.currentPayByIso)}
          onChange={(e) => setPicked(e.target.value)}
          className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
          data-testid="pbx-new-date"
        />
      </label>

      <div className="rounded-md border border-blood/35 bg-blood/5 px-3 py-2.5" data-testid="pbx-consequence">
        <p className="label-small-caps text-blood">If you decline</p>
        <p className="font-serif text-xs text-cream-soft mt-1">
          The link keeps its original pay-by ({dayLabel(request.currentPayByIso)}) and lapses as normal if
          it isn&rsquo;t paid by then; the follow-up stays with {OWNER_LABEL[owner]}.
        </p>
      </div>

      <label className="block">
        <span className="label-small-caps">Reason (required to decline)</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-blood resize-none"
          data-testid="pbx-reason"
        />
      </label>

      {error ? (
        <p className="font-inter text-xs text-blood" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={decline}
          disabled={!reason.trim()}
          className="inline-flex items-center gap-1.5 rounded-md border border-blood/50 bg-blood/10 px-3 py-1.5 text-xs text-blood hover:bg-blood/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pbx-decline"
        >
          <X size={13} /> Decline
        </button>
        <button
          type="button"
          onClick={extend}
          disabled={!extendable}
          className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pbx-extend"
        >
          <Check size={13} /> Extend
        </button>
      </div>
    </div>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <p className="label-small-caps">{k}</p>
      <p className="font-serif text-sm mt-0.5 text-cream">{v}</p>
    </div>
  );
}
